import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Outlet, useParams } from "react-router-dom";
import { useTemplate } from "@hooks";
import { TemplateDetail } from "./TemplateCreationContext";

interface TemplateEditState {
  templateId: string;
  templateName: string;
  setTemplateName: (name: string) => void;
  templateDetails: TemplateDetail[];
  addDetail: (detail: Omit<TemplateDetail, "localId">) => void;
  removeDetail: (localId: string) => void;
  updateDetail: (
    localId: string,
    detail: Omit<TemplateDetail, "localId">,
  ) => void;
  isLoading: boolean;
}

const TemplateEditContext = createContext<TemplateEditState | null>(null);

export function useTemplateEditContext() {
  const ctx = useContext(TemplateEditContext);
  if (!ctx) {
    throw new Error(
      "useTemplateEditContext must be used within a TemplateEditProvider",
    );
  }
  return ctx;
}

export function TemplateEditProvider() {
  const { templateId = "" } = useParams();
  const { data: template, isLoading } = useTemplate(templateId);

  const [templateName, setTemplateName] = useState("");
  const [templateDetails, setTemplateDetails] = useState<TemplateDetail[]>([]);
  const initialized = useRef(false);

  useEffect(() => {
    if (template && !initialized.current) {
      initialized.current = true;
      setTemplateName(template.name);
      setTemplateDetails(
        (template.prescriptionDetails ?? []).map((detail) => ({
          ...detail,
          localId: crypto.randomUUID(),
        })),
      );
    }
  }, [template]);

  const addDetail = useCallback((detail: Omit<TemplateDetail, "localId">) => {
    const localId = crypto.randomUUID();
    setTemplateDetails((prev) => [...prev, { ...detail, localId }]);
  }, []);

  const removeDetail = useCallback((localId: string) => {
    setTemplateDetails((prev) => prev.filter((d) => d.localId !== localId));
  }, []);

  const updateDetail = useCallback(
    (localId: string, detail: Omit<TemplateDetail, "localId">) => {
      setTemplateDetails((prev) =>
        prev.map((d) => (d.localId === localId ? { ...detail, localId } : d)),
      );
    },
    [],
  );

  const value = useMemo(
    () => ({
      templateId,
      templateName,
      setTemplateName,
      templateDetails,
      addDetail,
      removeDetail,
      updateDetail,
      isLoading,
    }),
    [
      templateId,
      templateName,
      templateDetails,
      addDetail,
      removeDetail,
      updateDetail,
      isLoading,
    ],
  );

  return (
    <TemplateEditContext.Provider value={value}>
      <Outlet />
    </TemplateEditContext.Provider>
  );
}
